export type SourceBadgeKind =
  | "default"
  | "pdf"
  | "ugl"
  | "datanorm"
  | "manual"
  | "identity"
  | "review"
  | "context"
  | "buyer";

type SourceBadgeSize = "xs" | "sm";

export function sourceBadgeKindFromSourceKind(
  sourceKind: string | null | undefined,
): SourceBadgeKind {
  const normalized = sourceKind?.toLowerCase() ?? "";
  if (normalized.startsWith("pdf")) return "pdf";
  if (normalized.startsWith("ugl")) return "ugl";
  if (normalized.startsWith("datanorm")) return "datanorm";
  if (normalized.startsWith("manual")) return "manual";
  return "default";
}

export function sourceBadgeClassName({
  inactive = false,
  interactive = false,
  size = "sm",
}: {
  inactive?: boolean;
  interactive?: boolean;
  size?: SourceBadgeSize;
} = {}): string {
  const sizeClass =
    size === "xs"
      ? "gap-1 rounded px-1.5 py-0.5 text-[10px]"
      : "gap-1.5 rounded-md px-2 py-0.5 text-xs";
  const toneClass = inactive
    ? "border-slate-200 bg-slate-50 text-slate-400"
    : "border-slate-200 bg-white text-slate-700";
  const interactionClass = interactive
    ? "transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
    : "";
  return [
    "inline-flex min-w-0 max-w-full items-center border font-medium leading-4",
    sizeClass,
    toneClass,
    interactionClass,
  ]
    .filter(Boolean)
    .join(" ");
}

export function SourceBadge({
  kind = "default",
  label,
  inactive = false,
  interactive = false,
  size = "sm",
  className = "",
  title,
}: {
  kind?: SourceBadgeKind;
  label: string;
  inactive?: boolean;
  interactive?: boolean;
  size?: SourceBadgeSize;
  className?: string;
  title?: string;
}) {
  return (
    <span
      className={`${sourceBadgeClassName({ inactive, interactive, size })} ${className}`}
      title={title}
    >
      <SourceIcon kind={kind} className={size === "xs" ? "h-3 w-3" : "h-3.5 w-3.5"} />
      <span className="min-w-0 truncate">{label}</span>
    </span>
  );
}

export function SourceIcon({
  kind,
  className = "h-3.5 w-3.5",
}: {
  kind: SourceBadgeKind;
  className?: string;
}) {
  const icon = sourceIconPath(kind);
  return (
    <svg
      className={`shrink-0 ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {icon}
    </svg>
  );
}

function sourceIconPath(kind: SourceBadgeKind) {
  switch (kind) {
    case "pdf":
      return (
        <>
          <path d="M14.25 2.5H6.75A1.75 1.75 0 005 4.25v15.5A1.75 1.75 0 006.75 21.5h10.5A1.75 1.75 0 0019 19.75V7.25z" />
          <path d="M14 2.75V7.5h4.75" />
          <path d="M8.25 13h7.5M8.25 16h5" />
        </>
      );
    case "ugl":
      return (
        <>
          <rect x="3.5" y="4.5" width="17" height="15" rx="1.75" />
          <path d="M3.5 9.5h17M8.75 4.5v15M14.25 4.5v15" />
        </>
      );
    case "datanorm":
      return (
        <>
          <ellipse cx="12" cy="5.5" rx="6.5" ry="2.5" />
          <path d="M5.5 5.5v6c0 1.38 2.91 2.5 6.5 2.5s6.5-1.12 6.5-2.5v-6" />
          <path d="M5.5 11.5v6c0 1.38 2.91 2.5 6.5 2.5s6.5-1.12 6.5-2.5v-6" />
        </>
      );
    case "manual":
      return (
        <>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4z" />
        </>
      );
    case "identity":
      return (
        <>
          <path d="M10 13a5 5 0 007.07 0l2.12-2.12a5 5 0 00-7.07-7.07L10.9 5" />
          <path d="M14 11a5 5 0 00-7.07 0L4.8 13.12a5 5 0 007.07 7.07L13.1 19" />
        </>
      );
    case "review":
      return (
        <>
          <circle cx="11" cy="11" r="6.5" />
          <path d="M16 16l4 4" />
        </>
      );
    case "buyer":
      return (
        <>
          <circle cx="12" cy="7" r="3.5" />
          <path d="M5.5 20a6.5 6.5 0 0113 0" />
        </>
      );
    case "context":
      return (
        <>
          <path d="M3.5 6.5A1.5 1.5 0 015 5h4l2 2h8a1.5 1.5 0 011.5 1.5v9A1.5 1.5 0 0119 19H5a1.5 1.5 0 01-1.5-1.5z" />
          <path d="M7 12h10M7 15h7" />
        </>
      );
    default:
      return (
        <>
          <circle cx="12" cy="12" r="7.5" />
          <path d="M12 8v4l2.5 2" />
        </>
      );
  }
}
